import { useState, useEffect } from 'react';
import { Plus, Play, Trash2, TrendingUp, TrendingDown, Copy, Edit2, FileDown, Lock, CheckSquare, XSquare, RotateCcw, AlertCircle } from 'lucide-react';
import { supabase, Scenario, BudgetLine, ScenarioBudgetLine, ScenarioCustomOperation, Category } from '../lib/supabase';
import { getCurrentSchoolYear, getSchoolYearLabel } from '../utils/schoolYear';
import { PDFExporter, formatCurrency, formatCurrencyForPDF } from '../utils/pdfExport';
import { useAuth } from '../contexts/AuthContext';
import { useSubscriptionAccess } from '../hooks/useSubscriptionAccess';
import { useFiscalYear } from '../hooks/useFiscalYear';
import { UpgradeModal } from './UpgradeModal';

export function ScenariosTab() {
  const { effectiveUserId } = useAuth();
  const { canAccessScenarios, maxScenarios, planTier, loading: accessLoading } = useSubscriptionAccess();
  const { startMonth, startDay, loading: fiscalYearLoading } = useFiscalYear();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [budgetLines, setBudgetLines] = useState<(BudgetLine & { category?: Category })[]>([]);
  const [selectedScenario, setSelectedScenario] = useState<string | null>(null);
  const [scenarioBudgetLines, setScenarioBudgetLines] = useState<ScenarioBudgetLine[]>([]);
  const [customOperations, setCustomOperations] = useState<(ScenarioCustomOperation & { category?: Category })[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCustomOpModal, setShowCustomOpModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedYear, setSelectedYear] = useState(0);
  const [newScenario, setNewScenario] = useState({ name: '', description: '' });
  const [editScenario, setEditScenario] = useState({ id: '', name: '', description: '' });
  const [newCustomOp, setNewCustomOp] = useState({
    label: '',
    amount: '',
    category_id: '',
    notes: ''
  });
  const [editingAmounts, setEditingAmounts] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!fiscalYearLoading && selectedYear === 0) {
      setSelectedYear(getCurrentSchoolYear(startMonth, startDay));
    }
  }, [fiscalYearLoading, startMonth, startDay]);

  useEffect(() => {
    if (selectedYear > 0) {
      loadScenarios();
      loadBudgetLines();
      loadCategories();
    }
  }, [selectedYear, effectiveUserId]);

  useEffect(() => {
    if (selectedScenario) {
      loadScenarioBudgetLines(selectedScenario);
      loadCustomOperations(selectedScenario);
      setEditingAmounts({});
    }
  }, [selectedScenario]);

  async function loadScenarios() {
    if (!effectiveUserId) return;

    const { data, error } = await supabase
      .from('scenarios')
      .select('*')
      .eq('user_id', effectiveUserId)
      .order('created_at', { ascending: false });

    if (!error && data) {
      setScenarios(data);
      if (data.length > 0 && !selectedScenario) {
        setSelectedScenario(data[0].id);
      }
    }
  }

  async function loadBudgetLines() {
    if (!effectiveUserId) return;

    const { data, error } = await supabase
      .from('budget_lines')
      .select('*, categories(*)')
      .eq('user_id', effectiveUserId)
      .eq('year', selectedYear)
      .order('planned_amount', { ascending: false });

    if (!error && data) {
      setBudgetLines(data.map(line => ({
        ...line,
        category: line.categories
      })));
    }
  }

  async function loadCategories() {
    if (!effectiveUserId) return;

    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .eq('user_id', effectiveUserId)
      .eq('is_internal_transfer', false)
      .order('name');

    if (!error && data) {
      setCategories(data);
    }
  }

  async function loadScenarioBudgetLines(scenarioId: string) {
    console.log('[loadScenarioBudgetLines] Loading for scenario:', scenarioId);
    const { data, error } = await supabase
      .from('scenario_budget_lines')
      .select('*')
      .eq('scenario_id', scenarioId);

    console.log('[loadScenarioBudgetLines] Result:', { count: data?.length, data, error });

    if (error) {
      console.error('[loadScenarioBudgetLines] Error:', error);
    } else if (data) {
      setScenarioBudgetLines(data);
    }
  }

  async function loadCustomOperations(scenarioId: string) {
    const { data, error } = await supabase
      .from('scenario_custom_operations')
      .select('*, categories(*)')
      .eq('scenario_id', scenarioId);

    if (!error && data) {
      setCustomOperations(data.map(op => ({
        ...op,
        category: op.categories
      })));
    }
  }

  async function createScenario() {
    if (!effectiveUserId || !newScenario.name.trim()) return;

    const { data, error } = await supabase
      .from('scenarios')
      .insert([{ ...newScenario, user_id: effectiveUserId }])
      .select()
      .single();

    if (!error && data) {
      const { error: budgetError } = await supabase
        .from('scenario_budget_lines')
        .insert(
          budgetLines.map(bl => ({
            scenario_id: data.id,
            budget_line_id: bl.id,
            is_included: true,
            adjusted_amount: null
          }))
        );

      if (!budgetError) {
        await loadScenarios();
        setNewScenario({ name: '', description: '' });
        setShowCreateModal(false);
        setSelectedScenario(data.id);
      }
    }
  }

  async function duplicateScenario(scenario: Scenario) {
    if (!effectiveUserId) return;

    const { data: newScenarioData, error: scenarioError } = await supabase
      .from('scenarios')
      .insert([{
        user_id: effectiveUserId,
        name: `${scenario.name} (copie)`,
        description: scenario.description,
        is_active: false
      }])
      .select()
      .single();

    if (scenarioError || !newScenarioData) return;

    const budgetLinesToCopy = scenarioBudgetLines.map(sbl => ({
      scenario_id: newScenarioData.id,
      budget_line_id: sbl.budget_line_id,
      is_included: sbl.is_included,
      adjusted_amount: sbl.adjusted_amount
    }));

    const customOpsToCopy = customOperations.map(op => ({
      scenario_id: newScenarioData.id,
      label: op.label,
      amount: op.amount,
      category_id: op.category_id,
      notes: op.notes
    }));

    if (budgetLinesToCopy.length > 0) {
      await supabase.from('scenario_budget_lines').insert(budgetLinesToCopy);
    }

    if (customOpsToCopy.length > 0) {
      await supabase.from('scenario_custom_operations').insert(customOpsToCopy);
    }

    await loadScenarios();
    setSelectedScenario(newScenarioData.id);
  }

  async function updateScenario() {
    if (!effectiveUserId || !editScenario.name.trim()) return;

    const { error } = await supabase
      .from('scenarios')
      .update({
        name: editScenario.name,
        description: editScenario.description
      })
      .eq('id', editScenario.id)
      .eq('user_id', effectiveUserId);

    if (!error) {
      await loadScenarios();
      setShowEditModal(false);
      setEditScenario({ id: '', name: '', description: '' });
    }
  }

  async function deleteScenario(id: string) {
    if (!effectiveUserId) return;

    const { error } = await supabase
      .from('scenarios')
      .delete()
      .eq('id', id)
      .eq('user_id', effectiveUserId);

    if (!error) {
      await loadScenarios();
      if (selectedScenario === id) {
        setSelectedScenario(null);
      }
    }
  }

  async function toggleBudgetLine(budgetLineId: string, currentIncluded: boolean) {
    if (!selectedScenario) return;

    console.log('[toggleBudgetLine] budgetLineId:', budgetLineId, 'currentIncluded:', currentIncluded);

    const scenarioBL = scenarioBudgetLines.find(
      sbl => sbl.budget_line_id === budgetLineId
    );

    console.log('[toggleBudgetLine] scenarioBL found:', scenarioBL);

    if (scenarioBL) {
      console.log('[toggleBudgetLine] UPDATE - setting is_included to:', !currentIncluded);
      const { data, error } = await supabase
        .from('scenario_budget_lines')
        .update({ is_included: !currentIncluded })
        .eq('id', scenarioBL.id)
        .select();

      console.log('[toggleBudgetLine] UPDATE result:', { data, error });

      if (error) {
        console.error('[toggleBudgetLine] UPDATE error:', error);
      } else {
        await loadScenarioBudgetLines(selectedScenario);
      }
    } else {
      console.log('[toggleBudgetLine] INSERT - is_included:', !currentIncluded);
      const { data, error } = await supabase
        .from('scenario_budget_lines')
        .insert([{
          scenario_id: selectedScenario,
          budget_line_id: budgetLineId,
          is_included: !currentIncluded,
          adjusted_amount: null
        }])
        .select();

      console.log('[toggleBudgetLine] INSERT result:', { data, error });

      if (error) {
        console.error('[toggleBudgetLine] INSERT error:', error);
      } else {
        await loadScenarioBudgetLines(selectedScenario);
      }
    }
  }

  async function saveAdjustedAmount(budgetLineId: string, amount: number | null) {
    if (!selectedScenario) return;

    console.log('[saveAdjustedAmount] budgetLineId:', budgetLineId, 'amount:', amount);

    const scenarioBL = scenarioBudgetLines.find(
      sbl => sbl.budget_line_id === budgetLineId
    );

    console.log('[saveAdjustedAmount] scenarioBL found:', scenarioBL);

    if (scenarioBL) {
      console.log('[saveAdjustedAmount] UPDATE adjusted_amount');
      const { data, error } = await supabase
        .from('scenario_budget_lines')
        .update({ adjusted_amount: amount })
        .eq('id', scenarioBL.id)
        .select();

      console.log('[saveAdjustedAmount] UPDATE result:', { data, error });
    } else {
      console.log('[saveAdjustedAmount] INSERT with is_included: true');
      const { data, error } = await supabase
        .from('scenario_budget_lines')
        .insert([{
          scenario_id: selectedScenario,
          budget_line_id: budgetLineId,
          is_included: true,
          adjusted_amount: amount
        }])
        .select();

      console.log('[saveAdjustedAmount] INSERT result:', { data, error });
    }

    await loadScenarioBudgetLines(selectedScenario);
  }

  async function createCustomOperation() {
    if (!effectiveUserId || !selectedScenario || !newCustomOp.label.trim() || !newCustomOp.amount) return;

    const { error } = await supabase
      .from('scenario_custom_operations')
      .insert([{
        user_id: effectiveUserId,
        scenario_id: selectedScenario,
        label: newCustomOp.label,
        amount: parseFloat(newCustomOp.amount),
        category_id: newCustomOp.category_id || null,
        notes: newCustomOp.notes
      }]);

    if (!error) {
      loadCustomOperations(selectedScenario);
      setNewCustomOp({ label: '', amount: '', category_id: '', notes: '' });
      setShowCustomOpModal(false);
    }
  }

  async function deleteCustomOperation(id: string) {
    if (!effectiveUserId) return;

    const { error } = await supabase
      .from('scenario_custom_operations')
      .delete()
      .eq('id', id)
      .eq('user_id', effectiveUserId);

    if (!error && selectedScenario) {
      loadCustomOperations(selectedScenario);
    }
  }

  async function includeAllLines(type: 'income' | 'expense' | 'all') {
    if (!selectedScenario) return;

    const linesToUpdate = budgetLines.filter(bl =>
      type === 'all' || bl.category?.type === type
    );

    for (const line of linesToUpdate) {
      const scenarioBL = scenarioBudgetLines.find(sbl => sbl.budget_line_id === line.id);

      if (scenarioBL) {
        await supabase
          .from('scenario_budget_lines')
          .update({ is_included: true })
          .eq('id', scenarioBL.id);
      } else {
        await supabase
          .from('scenario_budget_lines')
          .insert([{
            scenario_id: selectedScenario,
            budget_line_id: line.id,
            is_included: true,
            adjusted_amount: null
          }]);
      }
    }

    await loadScenarioBudgetLines(selectedScenario);
  }

  async function excludeAllLines(type: 'income' | 'expense' | 'all') {
    if (!selectedScenario) return;

    const linesToUpdate = budgetLines.filter(bl =>
      type === 'all' || bl.category?.type === type
    );

    for (const line of linesToUpdate) {
      const scenarioBL = scenarioBudgetLines.find(sbl => sbl.budget_line_id === line.id);

      if (scenarioBL) {
        await supabase
          .from('scenario_budget_lines')
          .update({ is_included: false })
          .eq('id', scenarioBL.id);
      } else {
        await supabase
          .from('scenario_budget_lines')
          .insert([{
            scenario_id: selectedScenario,
            budget_line_id: line.id,
            is_included: false,
            adjusted_amount: null
          }]);
      }
    }

    await loadScenarioBudgetLines(selectedScenario);
  }

  async function resetAllAdjustments(type: 'income' | 'expense' | 'all') {
    if (!selectedScenario) return;

    const linesToReset = budgetLines.filter(bl =>
      type === 'all' || bl.category?.type === type
    );

    for (const line of linesToReset) {
      const scenarioBL = scenarioBudgetLines.find(sbl => sbl.budget_line_id === line.id);

      if (scenarioBL) {
        await supabase
          .from('scenario_budget_lines')
          .update({ adjusted_amount: null })
          .eq('id', scenarioBL.id);
      }
    }

    await loadScenarioBudgetLines(selectedScenario);
  }

  const currentScenario = scenarios.find(s => s.id === selectedScenario);

  const budgetBalance = budgetLines.reduce((sum, budgetLine) => {
    const scenarioBL = scenarioBudgetLines.find(
      sbl => sbl.budget_line_id === budgetLine.id
    );

    const isIncluded = scenarioBL?.is_included ?? true;
    if (!isIncluded) return sum;

    const amount = scenarioBL?.adjusted_amount !== null && scenarioBL?.adjusted_amount !== undefined
      ? scenarioBL.adjusted_amount
      : Number(budgetLine.planned_amount);

    return sum + (budgetLine.category?.type === 'income' ? amount : -amount);
  }, 0);

  const customOpsBalance = customOperations.reduce((sum, op) => sum + Number(op.amount), 0);
  const scenarioBalance = budgetBalance + customOpsBalance;

  const realBudgetBalance = budgetLines.reduce((sum, bl) =>
    sum + (bl.category?.type === 'income' ? Number(bl.planned_amount) : -Number(bl.planned_amount)), 0
  );

  const impact = scenarioBalance - realBudgetBalance;

  const exportToPDF = () => {
    if (!currentScenario) return;

    const pdf = new PDFExporter();

    pdf.getDoc().text('Analyse de Scenario', pdf['pageWidth'] / 2, pdf['currentY'], { align: 'center' });
    pdf['currentY'] += 10;

    pdf.addSection(currentScenario.name);
    if (currentScenario.description) {
      pdf.addText(currentScenario.description, 10);
    }

    pdf.addSummaryBox([
      { label: 'Budget previsionnel de base', value: formatCurrencyForPDF(realBudgetBalance) },
      { label: 'Solde du scenario', value: formatCurrencyForPDF(scenarioBalance), highlight: true },
      { label: 'Impact du scenario', value: formatCurrencyForPDF(impact), highlight: true }
    ]);

    const includedIncomeLines = budgetLines.filter(line => {
      const scenarioBL = scenarioBudgetLines.find(sbl => sbl.budget_line_id === line.id);
      return line.category?.type === 'income' && (!scenarioBL || scenarioBL.is_included);
    });

    if (includedIncomeLines.length > 0) {
      pdf.addSection('Recettes incluses');
      const incomeData = includedIncomeLines.map(line => {
        const scenarioBL = scenarioBudgetLines.find(sbl => sbl.budget_line_id === line.id);
        const adjustedAmount = scenarioBL?.adjusted_amount !== null && scenarioBL?.adjusted_amount !== undefined
          ? scenarioBL.adjusted_amount
          : Number(line.planned_amount);
        return {
          categorie: line.category?.name || 'Sans categorie',
          montantBase: formatCurrencyForPDF(Number(line.planned_amount)),
          montantAjuste: scenarioBL?.adjusted_amount !== null && scenarioBL?.adjusted_amount !== undefined
            ? formatCurrencyForPDF(adjustedAmount)
            : '-'
        };
      });

      pdf.addTable(
        [
          { header: 'Categorie', dataKey: 'categorie' },
          { header: 'Montant de base', dataKey: 'montantBase' },
          { header: 'Montant ajuste', dataKey: 'montantAjuste' }
        ],
        incomeData,
        {
          columnStyles: {
            0: { halign: 'left', cellWidth: 70 },
            1: { halign: 'right', cellWidth: 50 },
            2: { halign: 'right', cellWidth: 50 }
          }
        }
      );
    }

    const includedExpenseLines = budgetLines.filter(line => {
      const scenarioBL = scenarioBudgetLines.find(sbl => sbl.budget_line_id === line.id);
      return line.category?.type === 'expense' && (!scenarioBL || scenarioBL.is_included);
    });

    if (includedExpenseLines.length > 0) {
      pdf.addSection('Depenses incluses');
      const expenseData = includedExpenseLines.map(line => {
        const scenarioBL = scenarioBudgetLines.find(sbl => sbl.budget_line_id === line.id);
        const adjustedAmount = scenarioBL?.adjusted_amount !== null && scenarioBL?.adjusted_amount !== undefined
          ? scenarioBL.adjusted_amount
          : Number(line.planned_amount);
        return {
          categorie: line.category?.name || 'Sans categorie',
          montantBase: formatCurrencyForPDF(Number(line.planned_amount)),
          montantAjuste: scenarioBL?.adjusted_amount !== null && scenarioBL?.adjusted_amount !== undefined
            ? formatCurrencyForPDF(adjustedAmount)
            : '-'
        };
      });

      pdf.addTable(
        [
          { header: 'Categorie', dataKey: 'categorie' },
          { header: 'Montant de base', dataKey: 'montantBase' },
          { header: 'Montant ajuste', dataKey: 'montantAjuste' }
        ],
        expenseData,
        {
          columnStyles: {
            0: { halign: 'left', cellWidth: 70 },
            1: { halign: 'right', cellWidth: 50 },
            2: { halign: 'right', cellWidth: 50 }
          }
        }
      );
    }

    if (customOperations.length > 0) {
      pdf.addSection('Operations personnalisees');
      const customData = customOperations.map(op => ({
        label: op.label,
        montant: formatCurrencyForPDF(Number(op.amount)),
        notes: op.notes || '-'
      }));

      pdf.addTable(
        [
          { header: 'Libelle', dataKey: 'label' },
          { header: 'Montant', dataKey: 'montant' },
          { header: 'Notes', dataKey: 'notes' }
        ],
        customData,
        {
          columnStyles: {
            0: { halign: 'left', cellWidth: 60 },
            1: { halign: 'right', cellWidth: 40 },
            2: { halign: 'left', cellWidth: 70 }
          }
        }
      );
    }

    pdf.generate({
      title: `Scenario: ${currentScenario.name}`,
      subtitle: `Annee scolaire ${getSchoolYearLabel(selectedYear)}`,
      schoolYear: getSchoolYearLabel(selectedYear)
    });
  };

  if (accessLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">Chargement...</div>
      </div>
    );
  }

  if (!canAccessScenarios) {
    return (
      <>
        <div className="flex flex-col items-center justify-center h-full py-12 px-4">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-2xl w-full text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock size={32} className="text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Vous voyez ce qui se passe aujourd'hui
            </h2>
            <p className="text-gray-600 mb-6">
              Les scénarios servent à comprendre ce qui va se passer dans différentes situations.
              Passez à l'offre <strong>Anticipation</strong> pour tester vos décisions.
            </p>
            <button
              onClick={() => setShowUpgradeModal(true)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Découvrir l'offre Anticipation
            </button>
          </div>
        </div>

        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          currentPlan={planTier}
          targetPlan="anticipation"
          blockedFeature="scenarios"
        />
      </>
    );
  }

  const canCreateMoreScenarios = scenarios.length < maxScenarios;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div className="flex-1">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800">Scénarios</h2>
          <p className="text-sm text-gray-600 mt-1">Simulez différents scénarios financiers</p>
          <div className="mt-2">
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {[2023, 2024, 2025, 2026, 2027].map(year => (
                <option key={year} value={year}>{getSchoolYearLabel(year)}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 sm:gap-3">
          {selectedScenario && (
            <button
              onClick={exportToPDF}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              <FileDown size={18} />
              <span className="hidden sm:inline">Exporter PDF</span>
              <span className="sm:hidden">PDF</span>
            </button>
          )}
          {!canCreateMoreScenarios && (
            <span className="text-xs sm:text-sm text-orange-600 bg-orange-50 px-2 sm:px-3 py-2 rounded">
              Limite de {maxScenarios} scénarios atteinte
            </span>
          )}
          <button
            onClick={() => canCreateMoreScenarios ? setShowCreateModal(true) : setShowUpgradeModal(true)}
            className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors text-sm ${
              canCreateMoreScenarios
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-400 text-white hover:bg-gray-500'
            }`}
          >
            <Plus size={20} />
            <span className="hidden sm:inline">{canCreateMoreScenarios ? 'Nouveau scénario' : 'Passer à Sécurité'}</span>
            <span className="sm:hidden">Nouveau</span>
          </button>
        </div>

        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          currentPlan={planTier}
          targetPlan="securite"
          blockedFeature="unlimited_scenarios"
        />
      </div>

      <div className="grid lg:grid-cols-4 gap-4">
        <div className="lg:col-span-1 space-y-2">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Mes scénarios</h3>
          {scenarios.map(scenario => (
            <div
              key={scenario.id}
              className={`p-4 rounded-lg cursor-pointer transition-all ${
                selectedScenario === scenario.id
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-white hover:bg-gray-50 border border-gray-200'
              }`}
              onClick={() => setSelectedScenario(scenario.id)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="font-medium">{scenario.name}</div>
                  {scenario.description && (
                    <div className={`text-sm mt-1 ${selectedScenario === scenario.id ? 'text-blue-100' : 'text-gray-500'}`}>
                      {scenario.description}
                    </div>
                  )}
                </div>
                <div className="flex gap-1 ml-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditScenario({ id: scenario.id, name: scenario.name, description: scenario.description });
                      setShowEditModal(true);
                    }}
                    className={`${selectedScenario === scenario.id ? 'text-white hover:text-blue-200' : 'text-gray-600 hover:text-gray-800'}`}
                    title="Renommer le scénario"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      duplicateScenario(scenario);
                    }}
                    className={`${selectedScenario === scenario.id ? 'text-white hover:text-blue-200' : 'text-blue-600 hover:text-blue-800'}`}
                    title="Dupliquer le scénario"
                  >
                    <Copy size={16} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteScenario(scenario.id);
                    }}
                    className={`${selectedScenario === scenario.id ? 'text-white hover:text-red-200' : 'text-red-600 hover:text-red-800'}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {scenarios.length === 0 && (
            <div className="text-center text-gray-500 py-8">
              Aucun scénario créé
            </div>
          )}
        </div>

        <div className="lg:col-span-3">
          {currentScenario ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-white rounded-lg shadow p-4 sm:p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-sm font-medium text-gray-700">Prévisionnel réel</p>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    Votre budget initial, sans modifications
                  </p>
                  <p className="text-xl sm:text-2xl font-bold break-words" style={{ color: realBudgetBalance >= 0 ? '#059669' : '#dc2626' }}>
                    {realBudgetBalance.toFixed(2)} €
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow p-4 sm:p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-sm font-medium text-gray-700">Scénario simulé</p>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    Budget après vos ajustements
                  </p>
                  <p className="text-xl sm:text-2xl font-bold break-words" style={{ color: scenarioBalance >= 0 ? '#059669' : '#dc2626' }}>
                    {scenarioBalance.toFixed(2)} €
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow p-4 sm:p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-sm font-medium text-gray-700">Impact du scénario</p>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    Différence entre réel et simulé
                  </p>
                  <div className="flex items-center gap-2">
                    {impact >= 0 ? <TrendingUp className="text-green-600" size={24} /> : <TrendingDown className="text-red-600" size={24} />}
                    <p className="text-xl sm:text-2xl font-bold break-words" style={{ color: impact >= 0 ? '#059669' : '#dc2626' }}>
                      {impact > 0 ? '+' : ''}{impact.toFixed(2)} €
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="font-medium text-blue-900 text-sm mb-1">
                      Comment utiliser les scénarios ?
                    </h4>
                    <p className="text-sm text-blue-700">
                      <strong>Décochez</strong> les lignes que vous ne souhaitez pas inclure dans ce scénario.
                      <strong> Modifiez</strong> les montants dans la colonne "Montant ajusté" pour simuler des variations.
                      Laissez vide pour conserver le montant prévu. Les lignes décochées n'impactent pas le solde du scénario.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-4 sm:px-6 py-3 bg-gray-50 border-b border-gray-200">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <h3 className="font-semibold text-gray-800">Produits (Recettes)</h3>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => includeAllLines('income')}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-green-700 bg-green-50 hover:bg-green-100 rounded transition-colors whitespace-nowrap"
                        title="Inclure toutes les recettes"
                      >
                        <CheckSquare size={14} />
                        <span className="hidden sm:inline">Tout inclure</span>
                        <span className="sm:hidden">Inclure</span>
                      </button>
                      <button
                        onClick={() => excludeAllLines('income')}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors whitespace-nowrap"
                        title="Exclure toutes les recettes"
                      >
                        <XSquare size={14} />
                        <span className="hidden sm:inline">Tout exclure</span>
                        <span className="sm:hidden">Exclure</span>
                      </button>
                      <button
                        onClick={() => resetAllAdjustments('income')}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-blue-700 bg-blue-50 hover:bg-blue-100 rounded transition-colors whitespace-nowrap"
                        title="Réinitialiser les montants ajustés"
                      >
                        <RotateCcw size={14} />
                        <span className="hidden sm:inline">Réinitialiser</span>
                        <span className="sm:hidden">Reset</span>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[600px]">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Inclus</th>
                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Catégorie</th>
                        <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Prévu</th>
                        <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ajusté</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {budgetLines.filter(bl => bl.category?.type === 'income').map(budgetLine => {
                        const scenarioBL = scenarioBudgetLines.find(
                          sbl => sbl.budget_line_id === budgetLine.id
                        );
                        const isIncluded = scenarioBL?.is_included ?? true;
                        const adjustedAmount = scenarioBL?.adjusted_amount;
                        const editingValue = editingAmounts[budgetLine.id];
                        const displayValue = editingValue !== undefined
                          ? editingValue
                          : (adjustedAmount !== null && adjustedAmount !== undefined ? adjustedAmount.toString() : '');

                        console.log('[RENDER Income]', budgetLine.category?.name, {
                          budgetLineId: budgetLine.id,
                          scenarioBL,
                          isIncluded,
                          adjustedAmount
                        });

                        return (
                          <tr key={budgetLine.id} className={!isIncluded ? 'opacity-50 bg-gray-50' : ''}>
                            <td className="px-3 sm:px-6 py-4">
                              <input
                                type="checkbox"
                                checked={isIncluded}
                                onChange={() => {
                                  console.log('[CHECKBOX CLICK Income]', budgetLine.category?.name, 'isIncluded:', isIncluded);
                                  toggleBudgetLine(budgetLine.id, isIncluded);
                                }}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                              />
                            </td>
                            <td className="px-3 sm:px-6 py-4 text-sm text-gray-900">{budgetLine.category?.name}</td>
                            <td className="px-3 sm:px-6 py-4 text-sm text-right font-medium text-green-600 whitespace-nowrap">
                              {Number(budgetLine.planned_amount).toFixed(2)} €
                            </td>
                            <td className="px-3 sm:px-6 py-4">
                              <input
                                type="number"
                                step="0.01"
                                value={displayValue}
                                placeholder={Number(budgetLine.planned_amount).toFixed(2)}
                                onChange={(e) => {
                                  setEditingAmounts(prev => ({
                                    ...prev,
                                    [budgetLine.id]: e.target.value
                                  }));
                                }}
                                onBlur={(e) => {
                                  const val = e.target.value;
                                  saveAdjustedAmount(
                                    budgetLine.id,
                                    val === '' ? null : parseFloat(val)
                                  );
                                  setEditingAmounts(prev => {
                                    const next = { ...prev };
                                    delete next[budgetLine.id];
                                    return next;
                                  });
                                }}
                                className="w-full px-2 sm:px-3 py-1 text-sm text-right border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-4 sm:px-6 py-3 bg-gray-50 border-b border-gray-200">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <h3 className="font-semibold text-gray-800">Charges (Dépenses)</h3>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => includeAllLines('expense')}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-green-700 bg-green-50 hover:bg-green-100 rounded transition-colors whitespace-nowrap"
                        title="Inclure toutes les dépenses"
                      >
                        <CheckSquare size={14} />
                        <span className="hidden sm:inline">Tout inclure</span>
                        <span className="sm:hidden">Inclure</span>
                      </button>
                      <button
                        onClick={() => excludeAllLines('expense')}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors whitespace-nowrap"
                        title="Exclure toutes les dépenses"
                      >
                        <XSquare size={14} />
                        <span className="hidden sm:inline">Tout exclure</span>
                        <span className="sm:hidden">Exclure</span>
                      </button>
                      <button
                        onClick={() => resetAllAdjustments('expense')}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-blue-700 bg-blue-50 hover:bg-blue-100 rounded transition-colors whitespace-nowrap"
                        title="Réinitialiser les montants ajustés"
                      >
                        <RotateCcw size={14} />
                        <span className="hidden sm:inline">Réinitialiser</span>
                        <span className="sm:hidden">Reset</span>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[600px]">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Inclus</th>
                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Catégorie</th>
                        <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Prévu</th>
                        <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ajusté</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {budgetLines.filter(bl => bl.category?.type === 'expense').map(budgetLine => {
                        const scenarioBL = scenarioBudgetLines.find(
                          sbl => sbl.budget_line_id === budgetLine.id
                        );
                        const isIncluded = scenarioBL?.is_included ?? true;
                        const adjustedAmount = scenarioBL?.adjusted_amount;
                        const editingValue = editingAmounts[budgetLine.id];
                        const displayValue = editingValue !== undefined
                          ? editingValue
                          : (adjustedAmount !== null && adjustedAmount !== undefined ? adjustedAmount.toString() : '');

                        console.log('[RENDER Expense]', budgetLine.category?.name, {
                          budgetLineId: budgetLine.id,
                          scenarioBL,
                          isIncluded,
                          adjustedAmount
                        });

                        return (
                          <tr key={budgetLine.id} className={!isIncluded ? 'opacity-50 bg-gray-50' : ''}>
                            <td className="px-3 sm:px-6 py-4">
                              <input
                                type="checkbox"
                                checked={isIncluded}
                                onChange={() => {
                                  console.log('[CHECKBOX CLICK Expense]', budgetLine.category?.name, 'isIncluded:', isIncluded);
                                  toggleBudgetLine(budgetLine.id, isIncluded);
                                }}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                              />
                            </td>
                            <td className="px-3 sm:px-6 py-4 text-sm text-gray-900">{budgetLine.category?.name}</td>
                            <td className="px-3 sm:px-6 py-4 text-sm text-right font-medium text-red-600 whitespace-nowrap">
                              {Number(budgetLine.planned_amount).toFixed(2)} €
                            </td>
                            <td className="px-3 sm:px-6 py-4">
                              <input
                                type="number"
                                step="0.01"
                                value={displayValue}
                                placeholder={Number(budgetLine.planned_amount).toFixed(2)}
                                onChange={(e) => {
                                  setEditingAmounts(prev => ({
                                    ...prev,
                                    [budgetLine.id]: e.target.value
                                  }));
                                }}
                                onBlur={(e) => {
                                  const val = e.target.value;
                                  saveAdjustedAmount(
                                    budgetLine.id,
                                    val === '' ? null : parseFloat(val)
                                  );
                                  setEditingAmounts(prev => {
                                    const next = { ...prev };
                                    delete next[budgetLine.id];
                                    return next;
                                  });
                                }}
                                className="w-full px-2 sm:px-3 py-1 text-sm text-right border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-4 sm:px-6 py-3 bg-gray-50 border-b border-gray-200 flex flex-col sm:flex-row justify-between sm:items-center gap-2">
                  <h3 className="font-semibold text-gray-800">Opérations personnalisées</h3>
                  <button
                    onClick={() => setShowCustomOpModal(true)}
                    className="flex items-center gap-2 px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm self-start sm:self-auto"
                  >
                    <Plus size={16} />
                    Ajouter
                  </button>
                </div>
                <div className="overflow-x-auto">
                  {customOperations.length > 0 ? (
                    <table className="w-full min-w-[500px]">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Libellé</th>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Catégorie</th>
                          <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Montant</th>
                          <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {customOperations.map(op => (
                          <tr key={op.id}>
                            <td className="px-3 sm:px-6 py-4 text-sm text-gray-900">
                              {op.label}
                              {op.notes && <div className="text-xs text-gray-500">{op.notes}</div>}
                            </td>
                            <td className="px-3 sm:px-6 py-4 text-sm text-gray-600">
                              {op.category?.name || 'Non catégorisée'}
                            </td>
                            <td className="px-3 sm:px-6 py-4 text-sm text-right font-medium whitespace-nowrap" style={{ color: Number(op.amount) >= 0 ? '#059669' : '#dc2626' }}>
                              {Number(op.amount).toFixed(2)} €
                            </td>
                            <td className="px-3 sm:px-6 py-4 text-right">
                              <button
                                onClick={() => deleteCustomOperation(op.id)}
                                className="text-red-600 hover:text-red-800"
                              >
                                <Trash2 size={16} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="px-4 sm:px-6 py-8 text-center text-gray-500">
                      Aucune opération personnalisée
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
              <Play size={48} className="mx-auto mb-4 text-gray-400" />
              <p>Sélectionnez ou créez un scénario pour commencer</p>
            </div>
          )}
        </div>
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full">
            <h3 className="text-lg sm:text-xl font-semibold mb-4">Créer un scénario</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom du scénario</label>
                <input
                  type="text"
                  value={newScenario.name}
                  onChange={(e) => setNewScenario({ ...newScenario, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Optimiste, Pessimiste..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newScenario.description}
                  onChange={(e) => setNewScenario({ ...newScenario, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Description optionnelle..."
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewScenario({ name: '', description: '' });
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={createScenario}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Créer
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full">
            <h3 className="text-lg sm:text-xl font-semibold mb-4">Modifier le scénario</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom du scénario</label>
                <input
                  type="text"
                  value={editScenario.name}
                  onChange={(e) => setEditScenario({ ...editScenario, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Optimiste, Pessimiste..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={editScenario.description}
                  onChange={(e) => setEditScenario({ ...editScenario, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Description optionnelle..."
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditScenario({ id: '', name: '', description: '' });
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={updateScenario}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {showCustomOpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg sm:text-xl font-semibold mb-4">Ajouter une opération personnalisée</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Libellé</label>
                <input
                  type="text"
                  value={newCustomOp.label}
                  onChange={(e) => setNewCustomOp({ ...newCustomOp, label: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Subvention exceptionnelle..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Montant (€) - positif pour recette, négatif pour dépense
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={newCustomOp.amount}
                  onChange={(e) => setNewCustomOp({ ...newCustomOp, amount: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie (optionnel)</label>
                <select
                  value={newCustomOp.category_id}
                  onChange={(e) => setNewCustomOp({ ...newCustomOp, category_id: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Non catégorisée</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name} ({cat.type === 'income' ? 'Recette' : 'Dépense'})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optionnel)</label>
                <textarea
                  value={newCustomOp.notes}
                  onChange={(e) => setNewCustomOp({ ...newCustomOp, notes: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Notes additionnelles..."
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCustomOpModal(false);
                  setNewCustomOp({ label: '', amount: '', category_id: '', notes: '' });
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={createCustomOperation}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Ajouter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
